import { LEGIT_SERVICE_CONFIG } from "common/constants";
import Spinner from "components/Spinner";
import React, { useEffect, useState } from "react";
import { connect } from "react-redux";
import { RootAction, RootState } from "StoreTypes";
import { removeApiToken } from "components/APITokenPersistence";
import { bindActionCreators, Dispatch } from "redux";
import { legit } from "apex-legit-service";
import InternalServerErrorState from "components/InternalServerErrorState";
import ErrorModalState from "components/ErrorModalState";
import FileNotFoundState from "components/FileNotFoundState";
import ErrorState from "components/ErrorState";
import ApexLogin from "../ApexLogin";
import { useFileSearch } from "../../hooks";
import { HTTPStatusCodes } from "../../types";
import { apiToken } from "../../services/legit/token";
import { config, params } from "../../utils";
import UnauthorizedState from "../../components/UnauthorizedState";

export interface Props {
  logout: () => void;
  token: string | null;
  isPublic: boolean;
}

const File = ({ logout, token, isPublic }: Props) => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [unauthorized, setUnauthorized] = useState(false);
  const [notFound, setNotFound] = useState(false);
  const [badRequest, setBadRequest] = useState(false);
  const [internalServerError, setInternalServerError] = useState(false);
  const [preconditionFail, setPreconditionFail] = useState(false);
  const { fileState, getFile } = useFileSearch();
  const authToken = token as string;

  useEffect(() => {
    if (!isPublic) {
      config.headers = {
        "Content-Type": "application/json",
        Authorization: authToken,
      };
      getFile(params, config);
    }
  }, []);

  useEffect(() => {
    handleResponse();
  });

  function handleResponse() {
    switch (fileState.status) {
      case HTTPStatusCodes.STATUS_REDIRECT:
        redirect(fileState.filename, fileState.signedUrl);
        setLoading(false);
        setUnauthorized(false);
        setNotFound(false);
        setInternalServerError(false);
        setError(false);
        setPreconditionFail(false);
        setBadRequest(false);

        break;
      case HTTPStatusCodes.STATUS_BAD_REQUEST:
        setBadRequest(true);
        setUnauthorized(false);
        setNotFound(false);
        setInternalServerError(false);
        setError(true);
        setLoading(false);
        setPreconditionFail(false);

        break;
      case HTTPStatusCodes.STATUS_UNAUTHORIZED:
        setUnauthorized(true);
        setNotFound(false);
        setInternalServerError(false);
        setError(true);
        setLoading(false);
        setPreconditionFail(false);
        setBadRequest(false);

        break;
      case HTTPStatusCodes.STATUS_NOT_FOUND:
        setNotFound(true);
        setInternalServerError(false);
        setError(true);
        setLoading(false);
        setPreconditionFail(false);
        setBadRequest(false);

        break;
      case HTTPStatusCodes.STATUS_PRECONDITION_FAILED:
        setNotFound(false);
        setUnauthorized(false);
        setPreconditionFail(true);
        setInternalServerError(false);
        setError(true);
        setBadRequest(false);
        removeApiToken();
        setLoading(false);
        logout();
        break;
      case HTTPStatusCodes.STATUS_INTERNAL_SERVER_ERROR:
        setInternalServerError(true);
        setUnauthorized(false);
        setNotFound(false);
        setError(true);
        setLoading(false);
        setPreconditionFail(false);
        setBadRequest(false);

        break;
      default:
        setLoading(false);
    }
  }

  const redirect = (fileName: string, signedUrl: string) => {
    const el = document.createElement("a");
    el.setAttribute("href", signedUrl);
    el.setAttribute("download", fileName);
    document.body.appendChild(el);
    el.download = fileName;
    el.click();
    el.remove();
  };

  const renderErrorState = () => {
    if (internalServerError) {
      return <InternalServerErrorState />;
    }
    if (badRequest) {
      return <ErrorModalState />;
    }
    if (notFound) {
      return <FileNotFoundState />;
    }
    if (unauthorized) {
      return <UnauthorizedState />;
    }
    if (preconditionFail) {
      return <ApexLogin />;
    }
    return <ErrorModalState />;
  };

  return (
    <>
      <Spinner loading={loading}>{error && <>{renderErrorState()}</>}</Spinner>
    </>
  );
};
const mapStateToProps = (state: RootState) => ({
  token: apiToken(state),
});

const mapDispatchToProps = (dispatch: Dispatch<RootAction>) =>
  bindActionCreators(
    {
      logout: () => legit.logout(LEGIT_SERVICE_CONFIG, { apexUser: true }),
    },
    dispatch
  );

export default connect(mapStateToProps, mapDispatchToProps)(File);
